<template>
	<CButton class="button--has-status" :disabled="internalStatus !== 'default'" :class="{['is-' + internalStatus]: internalStatus !== 'default'}" @click="$emit('click')">
		<template #icon><slot name="icon" /></template>

		<slot />

		<template #icon2><slot name="icon2" /></template>

		<template #status-icon>
			<Inline v-if="internalStatus === 'validating'" svg="cog" class="button--has-status__icon is-validating" />
			<Inline v-else-if="internalStatus === 'success'" svg="success" class="button--has-status__icon is-success" />
			<Inline v-else-if="internalStatus === 'error'" svg="error" class="button--has-status__icon is-error" />
		</template>
	</CButton>
</template>

<script lang="ts">
	import { defineComponent, type PropType } from 'vue';
	import CButton from './Button.vue';

	export type ButtonStatus = 'validating' | 'success' | 'error' | 'default' | 'disabled';

	export default defineComponent({
		name: 'CStatusButton',
		components: { CButton },
		props: {
			status: {
				type: String as PropType<ButtonStatus>,
				default: 'default'
			},
			autoReset: {
				type: Boolean,
				default: true
			},
			duration: {
				default: 2000,
				type: Number
			}
		},
		emits: ['click', 'reset-button-status'],
		data() {
			return {
				internalStatus: this.status,
				buttonTimeoutId: null
			};
		},
		watch: {
			status(newStatus) {
				// Set the new status
				this.internalStatus = newStatus;

				if (this.buttonTimeoutId) {
					clearTimeout(this.buttonTimeoutId);
				}

				// If the new status is anything other than validating (in progress) or disabled, wait
				// for the duration and then reset the button status to default
				if (this.autoReset && newStatus !== 'validating' && newStatus !== 'disabled') {
					this.buttonTimeoutId = setTimeout(() => {
						this.internalStatus = 'default';
						this.$emit('reset-button-status');
						this.buttonTimeoutId = null;
					}, this.duration);
				}
			}
		}
	});
</script>

<style lang="scss">
.button--has-status {
	position: relative;

	&__icon {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		top: 0;
		margin: auto !important;

		&.is-validating {
			animation: spin 1.5s infinite linear;
		}

		&.is-success {
			animation: tada .2s;
		}

		&.is-error {
			transform: scale(1.3);
		}
	}

	&[class*='is-'] {

		.button__text,
		.button__icon {
			opacity: .3;
		}
	}

	&:focus svg,
	&:not([disabled]):hover svg {
		transform: scale(1.1);
	}

	&.is-error {
		animation: shake 1s;
	}
}
</style>